import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { OwnerAdminCreator } from '@/dashboard/constants/roles';
import { UserRole, UserRoleType } from '@/dashboard/types/user_role';
import TeamUser from '@/dashboard/types/teamUser';
import type { CurrentUser } from '@/dashboard/types/current_user';

type UseAddTeamListProps = {
  team: TeamUser[];
  type: 'workspace' | 'project';
  currentUser: CurrentUser;
  onTeamUpdated: (team: TeamUser[]) => void;
};

export function useAddTeamList({ team, type, currentUser, onTeamUpdated }: UseAddTeamListProps) {
  const canAddCreators: ComputedRef<boolean> = computed(() => {
    const role = currentUser.role;
    return !!role && OwnerAdminCreator.includes(role);
  });
  const roles: ComputedRef<UserRoleType[]> = computed(() => {
    const baseRoles: UserRoleType[] = [UserRole.Viewer];

    if (canAddCreators.value) {
      baseRoles.unshift(UserRole.Creator); // add "creator" at the beginning
    }

    if (type !== 'workspace') {
      baseRoles.push(UserRole.Client); // add "client" only if type is not "workspace"
    }

    if (currentUser.role === UserRole.Owner && type === 'workspace') {
      baseRoles.unshift(UserRole.Admin);
    }

    return baseRoles;
  });
  const canAddUser = computed(() => validate());
  const role: Ref<UserRoleType> = ref(canAddCreators.value ? UserRole.Creator : UserRole.Viewer);
  const email: Ref<string> = ref('');
  const nameHasError: Ref<boolean> = ref(false);
  const currentTeam: Ref<TeamUser[]> = ref(team);

  function add() {
    if (validate()) {
      const user = new TeamUser(email.value, role.value, type, '', false);
      currentTeam.value = [...currentTeam.value, user];
      nameHasError.value = false;
      email.value = '';
    } else {
      nameHasError.value = true;
    }
  }

  function changeRole(newRole: UserRoleType, user: TeamUser) {
    const index: number = currentTeam.value.map((item) => item.email).indexOf(user.email);
    user.changeRole(newRole);

    if (index >= 0) {
      const items = currentTeam.value;
      items[index] = user;
      currentTeam.value = [...items];
    }
  }

  function remove(user: TeamUser, keepPersonalData: boolean) {
    const index = currentTeam.value.map((item) => item.email).indexOf(user.email);

    if (user.isAdded) {
      user.leave(keepPersonalData);
      const items = currentTeam.value;
      items[index] = user;
      currentTeam.value = [...items];
    } else {
      if (index >= 0) {
        currentTeam.value.splice(index, 1);
      }
    }
  }

  function validate(): boolean {
    if (email.value.length <= 0) {
      return false;
    } else if (!validateEmail(email.value)) {
      return false;
    } else {
      return currentTeam.value.map((item) => item.email).indexOf(email.value) < 0;
    }
  }

  function validateEmail(email: string) {
    // Define the regular expression for a valid email address
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // Test the email against the regular expression
    return re.test(String(email).toLowerCase());
  }

  watch(currentTeam, () => {
    const team = currentTeam.value;
    onTeamUpdated(team);
  });

  return {
    email,
    nameHasError,
    roles,
    role,
    canAddCreators,
    canAddUser,
    currentTeam,
    add,
    changeRole,
    remove,
  };
}
